<template>
  <div class="party-import-page">
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t('import_suppliers') }}</h4>
        </div>
      </div>
      <div class="page-btn">
        <router-link to="/inventory/supplier" class="btn btn-added">
          <i class="ti ti-arrow-left me-1"></i>Back
        </router-link>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-lg-7">
            <h6 class="mb-2">{{ $t('import_notes_title') }}</h6>
            <ul class="import-notes mb-0">
              <li>Use one sheet named <strong>Suppliers</strong>. Keep the header row from the sample.</li>
              <li><strong>name*</strong> is required and must be unique.</li>
              <li>
                <strong>opening_payable</strong> posts opening balance to Supplier Bills Payable
                (against Opening Balance Equity) on Confirm Import when &gt; 0.
              </li>
              <li>
                <span class="legend-swatch duplicate"></span> Red = duplicate name/phone.
                <span class="legend-swatch disqualified ms-2"></span> Orange = fix required.
              </li>
            </ul>
          </div>
          <div class="col-lg-5">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <a href="/samples/supplier_import_sample.xlsx" class="btn btn-outline-primary btn-md" download>
                <i class="ti ti-file-spreadsheet me-1"></i>{{ $t('download_sample') }}
              </a>
            </div>
            <label class="form-label">{{ $t('browse_import_file') }}</label>
            <div class="input-group">
              <input
                type="file"
                class="form-control"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                @change="onFileChosen"
              />
              <button type="button" class="btn btn-primary" :disabled="!file || loadingPreview" @click="loadFile">
                <span v-if="loadingPreview" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('load_import_file') }}
              </button>
            </div>
            <small v-if="file" class="text-muted">{{ file.name }}</small>
            <div v-if="errorMessage" class="alert alert-danger mt-2 mb-0 py-2">{{ errorMessage }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <span class="import-stat import-stat-ok">
              <i class="ti ti-circle-check"></i>{{ $t('import_legend_ok') }}: <strong>{{ summary.ok }}</strong>
            </span>
            <span class="import-stat import-stat-duplicate">
              <i class="ti ti-copy"></i>{{ $t('import_legend_duplicate') }}: <strong>{{ summary.duplicate }}</strong>
            </span>
            <span class="import-stat import-stat-disqualified">
              <i class="ti ti-alert-triangle"></i>{{ $t('import_legend_disqualified') }}: <strong>{{ summary.disqualified }}</strong>
            </span>
            <span v-if="revalidating" class="text-muted small">
              <span class="spinner-border spinner-border-sm me-1"></span>{{ $t('revalidating') }}
            </span>
          </div>
          <div class="d-flex flex-wrap gap-2 import-actions">
            <button type="button" class="btn btn-action-select" :disabled="!rows.length" @click="selectAllEligible">
              <i class="ti ti-checkbox me-1"></i>{{ $t('select_all_eligible') }}
            </button>
            <button type="button" class="btn btn-action-clear" :disabled="!rows.length" @click="clearSelection">
              <i class="ti ti-deselect me-1"></i>{{ $t('clear_selection') }}
            </button>
            <button
              type="button"
              class="btn btn-action-confirm"
              :disabled="!selectedCount || confirming"
              @click="confirmImport"
            >
              <span v-if="confirming" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ti ti-database-import me-1"></i>
              {{ $t('confirm_import') }}
              <span class="confirm-count">{{ selectedCount }}</span>
            </button>
          </div>
        </div>

        <div v-if="!rows.length" class="text-center text-muted py-5">{{ $t('no_rows_loaded') }}</div>
        <div v-else class="import-grid-wrap">
          <table class="table table-bordered import-grid mb-0" style="min-width: 900px">
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" :checked="allEligibleSelected" @change="toggleSelectAll($event)" />
                </th>
                <th>Status</th>
                <th>Issues</th>
                <th>name*</th>
                <th>phone_number</th>
                <th>address</th>
                <th>opening_payable</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.row_id" :class="rowClass(row)">
                <td class="col-check">
                  <input
                    type="checkbox"
                    :disabled="!row.eligible"
                    :checked="!!row.selected"
                    @change="toggleRow(row, $event)"
                  />
                </td>
                <td><span class="status-pill" :class="row.status">{{ row.status }}</span></td>
                <td class="issues-cell" :title="(row.issues || []).join('; ')">{{ (row.issues || []).join('; ') }}</td>
                <td><input v-model="row.name" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.phone_number" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.address" class="form-control form-control-sm" @change="scheduleValidate" /></td>
                <td><input v-model="row.opening_payable" class="form-control form-control-sm" @change="scheduleValidate" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import apiUrls from '@/urls';

export default {
  name: 'ImportSupplier',
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      file: null,
      rows: [],
      summary: { total: 0, ok: 0, duplicate: 0, disqualified: 0 },
      loadingPreview: false,
      revalidating: false,
      confirming: false,
      errorMessage: '',
      validateTimer: null,
    };
  },
  computed: {
    selectedCount() {
      return this.rows.filter((r) => r.selected && r.eligible).length;
    },
    allEligibleSelected() {
      const eligible = this.rows.filter((r) => r.eligible);
      return eligible.length > 0 && eligible.every((r) => r.selected);
    },
  },
  beforeUnmount() {
    if (this.validateTimer) clearTimeout(this.validateTimer);
  },
  methods: {
    rowClass(row) {
      return {
        'row-duplicate': row.status === 'duplicate',
        'row-disqualified': row.status === 'disqualified',
        'row-ok': row.status === 'ok',
      };
    },
    onFileChosen(event) {
      this.file = (event.target.files && event.target.files[0]) || null;
      this.errorMessage = '';
    },
    applyPreview(data) {
      this.rows = (data.rows || []).map((row) => ({
        ...row,
        issues: row.issues || [],
      }));
      this.summary = data.summary || { total: 0, ok: 0, duplicate: 0, disqualified: 0 };
    },
    async loadFile() {
      if (!this.file) return;
      this.loadingPreview = true;
      this.errorMessage = '';
      try {
        const form = new FormData();
        form.append('file', this.file);
        const { data } = await this.$axios.post(apiUrls.inventory_supplier.import_preview, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.applyPreview(data);
      } catch (err) {
        this.rows = [];
        const detail = err?.response?.data;
        this.errorMessage = detail?.file?.[0] || detail?.error || detail?.detail || 'Failed to load file.';
      } finally {
        this.loadingPreview = false;
      }
    },
    scheduleValidate() {
      if (this.validateTimer) clearTimeout(this.validateTimer);
      this.validateTimer = setTimeout(() => this.revalidate(), 450);
    },
    payloadRows() {
      return this.rows.map((row) => ({
        row_id: row.row_id,
        excel_row: row.excel_row,
        name: row.name,
        phone_number: row.phone_number,
        address: row.address,
        opening_payable: row.opening_payable,
        selected: !!row.selected,
      }));
    },
    async revalidate() {
      if (!this.rows.length) return;
      this.revalidating = true;
      try {
        const { data } = await this.$axios.post(apiUrls.inventory_supplier.import_validate, {
          rows: this.payloadRows(),
        });
        this.applyPreview(data);
      } catch (err) {
        this.errorMessage = err?.response?.data?.detail || 'Validation failed.';
      } finally {
        this.revalidating = false;
      }
    },
    toggleRow(row, event) {
      row.selected = !!event.target.checked && row.eligible;
    },
    toggleSelectAll(event) {
      const on = !!event.target.checked;
      this.rows.forEach((row) => {
        row.selected = on && row.eligible;
      });
    },
    selectAllEligible() {
      this.rows.forEach((row) => {
        row.selected = !!row.eligible;
      });
    },
    clearSelection() {
      this.rows.forEach((row) => {
        row.selected = false;
      });
    },
    async confirmImport() {
      this.confirming = true;
      this.errorMessage = '';
      try {
        const { data } = await this.$axios.post(apiUrls.inventory_supplier.import_confirm, {
          rows: this.payloadRows(),
        });
        if (data.preview) this.applyPreview(data.preview);
        const created = (data.created || []).length;
        const failed = (data.failed || []).length;
        if (created) {
          const done = new Set((data.created || []).map((c) => (c.name || '').toLowerCase()));
          this.rows = this.rows.filter((r) => !done.has((r.name || '').toLowerCase()));
          this.summary = {
            total: this.rows.length,
            ok: this.rows.filter((r) => r.status === 'ok').length,
            duplicate: this.rows.filter((r) => r.status === 'duplicate').length,
            disqualified: this.rows.filter((r) => r.status === 'disqualified').length,
          };
        }
        if (created && failed === 0) {
          this.toast.success(`${this.$t('import_success')}: ${created} supplier(s)`);
        } else if (created && failed > 0) {
          this.toast.warning(`${this.$t('import_success')}: ${created} supplier(s), ${failed} failed`);
        } else {
          this.toast.error(data.failed?.[0]?.error || data.message || 'Nothing imported.');
        }
      } catch (err) {
        const detail = err?.response?.data;
        this.toast.error(detail?.failed?.[0]?.error || detail?.error || 'Import failed.');
        if (detail?.preview) this.applyPreview(detail.preview);
      } finally {
        this.confirming = false;
      }
    },
  },
};
</script>

<style scoped src="../item/importShared.css"></style>
